// Published (api/published.ts): the words and checks behind the page, its
// key dialog and its calls, so the components stay thin. Pure:
// ./publishedModel.selftest.ts pins it.
//
//   Reachable  Name · Kind · Keys · State · Last call: everything a live
//              key can use (a workflow it runs, a saved result it reads).
//   Keys       Name · Can use · Ends · Last used · Calls today: "Read
//              ndx-morning-section, +1" / "Run Eurostoxx 50"; Never ·
//              27 Oct 2027 · Ends in 3 days (warning) · Ended; a revoked
//              key "Revoked 27 Oct".
//   Calls      When · Door · Did · Result · From.
//
// Days are London days, as the server counts them: a key given an end
// works to the end of that London day.

import type { ArtifactSummary } from "@/api/workflowArtifacts";
import type { ChangeKeyBody, GrantBody, GrantKind, KeyCall, KeyGrant, PublishedKeyView, PublishedOverview } from "@/api/published";
import { MONTHS, joinList, pluralize, toDate, type DateInput } from "@/lib/format";

import { savedResultState, stoppedWord, type StateWord } from "../artifacts/freshnessModel";

/** Grants of one key (the server's 422 too_many_grants). */
export const MAX_GRANTS = 20;
/** Workflows one key may run (phase 1; the server's 422 one_workflow). */
export const MAX_RUN_GRANTS = 1;

// ─── London days ─────────────────────────────────────────────────────────

const LONDON = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The London day of an instant: "2026-10-27". */
export function londonDay(at: Date): string {
  const p = Object.fromEntries(LONDON.formatToParts(at).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

const dayNumber = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
};

const dayFrom = (n: number) => new Date(n * 86_400_000).toISOString().slice(0, 10);

/** "27 Oct 2027"; `year: false` drops the year ("27 Oct"). */
export function dayWords(day: string, { year = true }: { year?: boolean } = {}): string {
  const [y, m, d] = day.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}${year ? ` ${y}` : ""}`;
}

// ─── when a key ends ─────────────────────────────────────────────────────

export type EndChoice = "never" | "30" | "90" | "year" | "keep";

/** The choices a new key's Ends offers. */
export const END_CHOICES: { value: EndChoice; label: string }[] = [
  { value: "never", label: "Never" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "year", label: "1 year" },
];

/** The London date a choice ends on, as `expires_on` takes it; null = never. */
export function expiresOnFor(choice: Exclude<EndChoice, "keep">, now: Date): string | null {
  if (choice === "never") return null;
  const today = londonDay(now);
  if (choice === "year") {
    const [y, m, d] = today.split("-").map(Number);
    // 29 Feb a year on is 28 Feb
    const last = new Date(Date.UTC(y + 1, m, 0)).getUTCDate();
    return `${y + 1}-${String(m).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
  }
  return dayFrom(dayNumber(today) + Number(choice));
}

/** The London day a key last works (`ends_at` is the next midnight), or null for never. */
export function lastDay(endsAt: string | null | undefined): string | null {
  if (!endsAt) return null;
  const end = toDate(endsAt);
  return end ? londonDay(new Date(end.getTime() - 1000)) : null;
}

export interface EndWord {
  text: string;
  tone: "warning" | "muted" | "plain";
  title?: string;
}

/** A key that has an end it has passed (an end that cannot be read counts as passed, as on the server). */
export function hasEnded(k: Pick<PublishedKeyView, "ends_at">, now: Date): boolean {
  if (!k.ends_at) return false;
  const end = toDate(k.ends_at);
  return !end || now.getTime() >= end.getTime();
}

/** Live: not revoked and not ended. */
export const isLive = (k: Pick<PublishedKeyView, "revoked_at" | "ends_at">, now: Date) => !k.revoked_at && !hasEnded(k, now);

/** Never · 27 Oct 2027 · Ends today / tomorrow / in 3 days (warning, within 7 days) · Ended. */
export function endWord(k: Pick<PublishedKeyView, "ends_at">, now: Date): EndWord {
  if (!k.ends_at) return { text: "Never", tone: "plain" };
  const day = lastDay(k.ends_at);
  if (hasEnded(k, now) || !day)
    return {
      text: "Ended",
      tone: "muted",
      title: day ? dayWords(day) : undefined,
    };
  const left = dayNumber(day) - dayNumber(londonDay(now));
  if (left > 7) return { text: dayWords(day), tone: "plain" };
  const text = left <= 0 ? "Ends today" : left === 1 ? "Ends tomorrow" : `Ends in ${left} days`;
  return { text, tone: "warning", title: dayWords(day) };
}

/** "Revoked 27 Oct" (the year only when it is not this one). */
export function revokedWord(revokedAt: DateInput, now: Date): string {
  const at = toDate(revokedAt);
  if (!at) return "Revoked";
  const day = londonDay(at);
  return `Revoked ${dayWords(day, { year: day.slice(0, 4) !== londonDay(now).slice(0, 4) })}`;
}

// ─── what a key can use ──────────────────────────────────────────────────

const runs = (g: KeyGrant) => g.kind === "workflow" && g.can.includes("run");
const reads = (g: KeyGrant) => g.kind === "result" && g.can.includes("read");

/** One grant in words: "Run Eurostoxx 50" / "Read ndx-morning-section"; "" for a kind this page does not know. */
export function grantWord(g: KeyGrant, workflowName: (slug: string) => string): string {
  if (runs(g)) return `Run ${workflowName(g.ref)}`;
  if (reads(g)) return `Read ${g.ref}`;
  return "";
}

/** The Can use cell: the run grant first, then the reads, "+N" for the rest; `full` lists them all (its tooltip). */
export function canUseWords(grants: readonly KeyGrant[], workflowName: (slug: string) => string): { short: string; full: string } {
  const words = [...grants.filter(runs), ...grants.filter(reads)].map((g) => grantWord(g, workflowName));
  if (!words.length) return { short: "Nothing", full: "" };
  return {
    short: words.length > 1 ? `${words[0]}, +${words.length - 1}` : words[0],
    full: words.join(", "),
  };
}

// ─── the Keys table ──────────────────────────────────────────────────────

export interface KeyRow {
  id: string;
  name: string;
  canUse: { short: string; full: string };
  /** The deployment the key belongs to (made and changed there; read-only here, still revocable), or null. */
  deployment: string | null;
  /** Ends, or for a revoked key "Revoked 27 Oct". */
  ends: EndWord;
  revoked: boolean;
  lastUsedAt: string | null;
  callsToday: number;
}

const time = (v: DateInput) => toDate(v)?.getTime() ?? 0;

/** Live keys newest first, then ended ones; revoked ones last (latest revoked first), only when asked for. */
export function keyRows(keys: readonly PublishedKeyView[], now: Date, workflowName: (slug: string) => string, { revoked = false } = {}): KeyRow[] {
  const open = keys.filter((k) => !k.revoked_at).sort((a, b) => Number(hasEnded(a, now)) - Number(hasEnded(b, now)) || time(b.created_at) - time(a.created_at));
  const gone = revoked ? keys.filter((k) => !!k.revoked_at).sort((a, b) => time(b.revoked_at) - time(a.revoked_at)) : [];
  return [...open, ...gone].map((k) => ({
    id: k.id,
    name: k.name,
    canUse: deploymentOf(k) ? { short: `Deployment ${deploymentOf(k)}`, full: `Deployment ${deploymentOf(k)}` } : canUseWords(k.grants ?? [], workflowName),
    deployment: deploymentOf(k),
    ends: k.revoked_at ? { text: revokedWord(k.revoked_at, now), tone: "muted" } : endWord(k, now),
    revoked: !!k.revoked_at,
    lastUsedAt: k.last_used_at ?? null,
    callsToday: k.calls_today ?? 0,
  }));
}

/** A key's deployment (keys.py rows carry `deployment`; the Published row passes it on once the server sends it), or null. */
export function deploymentOf(k: PublishedKeyView): string | null {
  const name = (k as PublishedKeyView & { deployment?: unknown }).deployment;
  return typeof name === "string" && name ? name : null;
}

/** The keys of a saved result as the server works them out (the overview's `things`: a grant counts against the workflow that saved the result, which the client cannot see). */
function serverKeys(view: Pick<PublishedOverview, "keys" | "things">, name: string, which: "keys" | "moved", now: Date): PublishedKeyView[] {
  const thing = (view.things ?? []).find((t) => t.kind === "result" && t.ref === name);
  const ids = new Set((thing?.[which] ?? []).map((k) => k.id));
  return view.keys.filter((k) => ids.has(k.id) && isLive(k, now));
}

/** Keys that read `name` now, as the server serves them: the saved result's "Read by". */
export function readersOf(view: Pick<PublishedOverview, "keys" | "things">, name: string, now: Date): PublishedKeyView[] {
  return serverKeys(view, name, "keys", now);
}

// ─── the Reachable table ─────────────────────────────────────────────────

export interface ThingRow {
  id: string;
  kind: GrantKind;
  ref: string;
  name: string;
  kindWord: "Workflow" | "Saved result";
  /** Live keys that reach it. */
  keys: number;
  /** Saved results only. */
  state: StateWord | null;
  stopped: StateWord | null;
  lastCallAt: string | null;
  /** Live keys that reach it (Unpublish takes it from them). */
  liveKeys: PublishedKeyView[];
  /** Live keys whose read grant was given while another workflow saved it (Remove takes it from them). */
  movedKeys: PublishedKeyView[];
}

/**
 * Everything a live key reaches, workflows first, then saved results, by
 * name. Built from the keys' grants (what the server lists as `things`
 * adds its name and last call, and for a saved result the keys granted it
 * while another workflow saved it, `moved`); a saved result's state comes
 * from the saved result itself (`artifact`).
 */
export function thingRows(
  view: Pick<PublishedOverview, "keys" | "things">,
  now: Date,
  {
    workflowName,
    artifact,
  }: {
    workflowName: (slug: string) => string;
    artifact: (name: string) => ArtifactSummary | undefined;
  },
): ThingRow[] {
  const live = view.keys.filter((k) => isLive(k, now));
  const rows = new Map<string, ThingRow>();
  const add = (kind: GrantKind, ref: string) => {
    const id = `${kind}:${ref}`;
    let row = rows.get(id);
    if (!row) {
      row = {
        id,
        kind,
        ref,
        name: kind === "workflow" ? workflowName(ref) : ref,
        kindWord: kind === "workflow" ? "Workflow" : "Saved result",
        keys: 0,
        state: null,
        stopped: null,
        lastCallAt: null,
        liveKeys: [],
        movedKeys: [],
      };
      rows.set(id, row);
    }
    return row;
  };
  for (const k of live)
    for (const g of k.grants ?? [])
      if (runs(g) || reads(g)) {
        const row = add(g.kind as GrantKind, g.ref);
        row.keys += 1;
        if (!row.liveKeys.includes(k)) row.liveKeys.push(k);
      }
  for (const t of view.things ?? []) {
    if (t.kind !== "workflow" && t.kind !== "result") continue;
    const row = add(t.kind, t.ref);
    if (t.kind === "workflow" && t.name) row.name = t.name;
    row.lastCallAt = t.last_call_at ?? null;
    if ((t.keys?.length ?? 0) > row.keys) row.keys = t.keys?.length ?? 0;
  }
  for (const row of rows.values()) {
    if (row.kind !== "result") continue;
    const a = artifact(row.ref);
    row.movedKeys = serverKeys(view, row.ref, "moved", now);
    row.state = a
      ? savedResultState(
          {
            published: a.published ?? true,
            freshness: a.freshness,
            moved: row.movedKeys.length > 0,
          },
          { unsaved: true },
        )
      : null;
    row.stopped = a ? stoppedWord(a.freshness) : null;
  }
  const order = (r: ThingRow) => (r.kind === "workflow" ? 0 : 1);
  return [...rows.values()].filter((r) => r.keys > 0).sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
}

/** Unpublish's and Remove's confirm: which keys lose it, and which of them are revoked for want of anything else. */
export function takeOffConfirm(verb: "Unpublish" | "Remove", t: Pick<ThingRow, "kind" | "ref" | "name">, from: PublishedKeyView[]) {
  const emptied = from.filter((k) => !grantsWithout(k, t.kind, t.ref));
  const revoked = emptied.length
    ? ` ${joinList(emptied.map((k) => `“${k.name}”`))} can use nothing else, so ${emptied.length === 1 ? "it is" : "they are"} revoked.`
    : "";
  return {
    title: verb === "Unpublish" ? `Unpublish “${t.name}”?` : `Remove “${t.name}” from ${pluralize(from.length, "key")}?`,
    body: verb === "Unpublish" ? `Callers using it stop at once.${revoked}` : revoked.trim() || undefined,
    confirmLabel: verb,
    danger: true,
  };
}

/** What Remove does to a key that reads a moved result: the grants left, or null when none is (the key is revoked). */
export function grantsWithout(k: Pick<PublishedKeyView, "grants">, kind: GrantKind, ref: string): GrantBody[] | null {
  const left = bodyGrants(k.grants ?? []).filter((g) => !(g.kind === kind && g.ref === ref));
  return left.length ? left : null;
}

// ─── a new key, a change ─────────────────────────────────────────────────

export interface KeyDraft {
  name: string;
  /** Workflows it runs (one at most). */
  run: string[];
  /** Saved results it reads. */
  read: string[];
  ends: EndChoice;
}

export const PICK_SOMETHING = "Pick something it can use.";
export const ONE_WORKFLOW = "A key runs at most one workflow.";
export const TOO_MANY = `A key can use at most ${MAX_GRANTS} things.`;

/** What the lists say is wrong as they are ticked (the empty case only on Create: nothing is wrong yet). */
export function pickProblem(d: Pick<KeyDraft, "run" | "read">): string | null {
  if (d.run.length > MAX_RUN_GRANTS) return ONE_WORKFLOW;
  if (d.run.length + d.read.length > MAX_GRANTS) return TOO_MANY;
  return null;
}

/** The grants a draft sends: the run first, then the reads. */
export function draftGrants(d: Pick<KeyDraft, "run" | "read">): GrantBody[] {
  return [...d.run.map((ref) => ({ kind: "workflow" as const, ref, can: ["run"] })), ...d.read.map((ref) => ({ kind: "result" as const, ref, can: ["read"] }))];
}

/** A key's grants as a change sends them back (a later build's kinds are kept by the server, never sent). */
export function bodyGrants(grants: readonly KeyGrant[]): GrantBody[] {
  return draftGrants({
    run: grants.filter(runs).map((g) => g.ref),
    read: grants.filter(reads).map((g) => g.ref),
  });
}

/** The draft a Change dialog starts from. */
export function draftFromKey(k: Pick<PublishedKeyView, "name" | "grants" | "ends_at">): KeyDraft {
  const g = k.grants ?? [];
  return {
    name: k.name,
    run: g.filter(runs).map((x) => x.ref),
    read: g.filter(reads).map((x) => x.ref),
    ends: k.ends_at ? "keep" : "never",
  };
}

/** Whether a dialog may tick `ref`: always while workflows are offered; off, only what the key held when it opened (the server's 403 for anything new; taking away and the end still work). */
export function canTick(offered: boolean, held: readonly string[], ref: string): boolean {
  return offered || held.includes(ref);
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Only what changed (no `expires_on` keeps the end); null when nothing did. */
export function changeBody(before: KeyDraft, after: KeyDraft, now: Date): ChangeKeyBody | null {
  const body: ChangeKeyBody = {};
  if (!sameSet(before.run, after.run) || !sameSet(before.read, after.read)) body.grants = draftGrants(after);
  if (after.ends !== before.ends && after.ends !== "keep") body.expires_on = expiresOnFor(after.ends, now);
  return Object.keys(body).length ? body : null;
}

/** One sentence for a refused create or change; `fallback` (the server's message) for anything else. A change's name_taken is the server's own words (another key shares the name, so it cannot be given a workflow to run). */
export function keyError(code: string | null, fallback: string, { change = false }: { change?: boolean } = {}): string {
  switch (code) {
    case "name_taken":
      return change ? fallback : "That name is taken.";
    case "one_workflow":
      return ONE_WORKFLOW;
    case "too_many_grants":
      return TOO_MANY;
    case "publish_results_off":
      return "Saved results cannot be published in this workspace.";
    case "workflows_not_enabled":
      return "Workflows are off in this workspace.";
    case "key_revoked":
      return "This key is revoked.";
    case "key_changed":
    case "not_found":
      return "It changed meanwhile: reload and try again.";
  }
  return fallback;
}

/** The revoke confirm. */
export function revokeConfirm(name: string) {
  return {
    title: `Revoke “${name}”?`,
    body: "Callers using it stop at once.",
    confirmLabel: "Revoke",
    danger: true,
  } as const;
}

// ─── the calls ───────────────────────────────────────────────────────────

// calls.py DOORS: Deploy's keyless lines (try, chat) and Apps' (the gate's key calls, an app's door)
const DOORS: Record<string, string> = {
  api: "API",
  mcp: "MCP",
  link: "Link",
  try: "Try it",
  chat: "Chat",
  gate: "App",
  door: "App's door",
};
export const doorWord = (door: string) => DOORS[door] ?? door;

/** The `<kind>:<ref>` a call names. */
function target(call: Pick<KeyCall, "target">): { kind: string; ref: string } {
  const i = (call.target || "").indexOf(":");
  return i > 0 ? { kind: call.target.slice(0, i), ref: call.target.slice(i + 1) } : { kind: "", ref: "" };
}

/** Did: "Read ndx-morning-section v14", "File of ndx-morning-section v14", "Listed saved results", "Started a run", "MCP read_saved_result". */
export function didWord(call: Pick<KeyCall, "op" | "target" | "v">, workflowName: (slug: string) => string): string {
  const t = target(call);
  const v = call.v ? ` v${call.v}` : "";
  const what = t.kind === "workflow" ? workflowName(t.ref) : t.ref;
  if (call.op.startsWith("tool:")) return `MCP ${call.op.slice(5)}`;
  switch (call.op) {
    case "describe":
      return what ? `Described ${what}` : "Asked what it can use";
    case "run":
      return "Started a run";
    case "runs":
      return "Listed runs";
    case "get_run":
      return "Read a run";
    case "cancel":
      return "Cancelled a run";
    case "run_file":
      return "File of a run";
    case "results":
      return "Listed saved results";
    case "read":
      return `Read ${what || "a saved result"}${v}`;
    case "read_file":
      return `File of ${what || "a saved result"}${v}`;
    case "tools_list":
      return "MCP list of tools";
    case "initialize":
      return "MCP connected";
  }
  return call.op || "Other";
}

/** Result: "OK" or "Refused (404)". */
export function resultWord(status: number): string {
  return status >= 200 && status < 400 ? "OK" : `Refused (${status || "?"})`;
}
